import React, { useContext, useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "./api";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, KeyRound, Mail, ShieldCheck, UserRound } from "lucide-react";
import { AuthContext } from "./AuthContext";
import { Button, Field, SectionHeader, SurfaceCard } from "./components/designSystem";

export default function AuthForm() {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(location.pathname !== "/signup");
  useEffect(() => {
    setIsLogin(location.pathname !== "/signup");
  }, [location.pathname]);
  const [form, setForm] = useState({ identifier: "", password: "", email: "" });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // Forgot password state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotResult, setForgotResult] = useState(null);
  const [forgotLoading, setForgotLoading] = useState(false);
  const hasForgotDebugResetDetails = Boolean(forgotResult?.token && forgotResult?.resetLink);
const searchParams = new URLSearchParams(location.search);

const redirect =
  searchParams.get("redirect") || "/app";

  // Password strength check
  function getPasswordStrength(pw) {
    if (!pw) return "";
    if (pw.length < 6) return "Too short";
    if (!/[A-Z]/.test(pw) || !/[0-9]/.test(pw)) return "Weak";
    if (pw.length >= 8 && /[A-Z]/.test(pw) && /[0-9]/.test(pw) && /[^A-Za-z0-9]/.test(pw)) return "Strong";
    return "Medium";
  }
  const passwordStrength = getPasswordStrength(form.password);
  const passwordTone = useMemo(() => {
    if (passwordStrength === "Strong") return "text-emerald-600";
    if (passwordStrength === "Medium") return "text-amber-600";
    if (passwordStrength) return "text-rose-600";
    return "text-slate-500";
  }, [passwordStrength]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!form.identifier || !form.password || (!isLogin && !form.email)) {
      setError(isLogin ? "Username or email and password required." : "Username, email, and password required.");
      return;
    }
    if (!isLogin) {
      if (form.password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
      if (passwordStrength === "Too short" || passwordStrength === "Weak") {
        setError("Password is too weak.");
        return;
      }
    }
    setLoading(true);
    try {
      const identifier = form.identifier.trim();
      const email = form.email.trim();
      const endpoint = isLogin ? "login" : "register";
      const payload = isLogin
        ? {
            ...(identifier.includes("@") ? { email: identifier } : { username: identifier }),
            password: form.password
          }
        : { username: identifier, password: form.password, email };
      const res = await fetch(`${API_BASE_URL}/api/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok) {
        if (isLogin && data.token) {
    login(data);

    navigate(
        decodeURIComponent(redirect),
        { replace: true }
    );
}
        if (!isLogin) {
          setSuccess(data.message || "Registration successful!");
          setForm({ identifier: "", password: "", email: "" });
          setConfirmPassword("");
          setTimeout(() => {
            setIsLogin(true);
            setSuccess("");
          }, 1200); // Redirect to login after 1.2s
        }
      } else {
        if (isLogin) {
          console.log("[LOGIN ERROR RESPONSE]", data);
        }
        setError(data.error || "Something went wrong.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    }
    setLoading(false);
  };

  // Forgot password submit
  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await res.json();
      setForgotResult(data);
    } catch (err) {
      setForgotResult({ error: "Network error" });
    }
    setForgotLoading(false);
  };

  return (
    <div className="min-h-[calc(100vh-140px)] bg-[#f3efe7] px-6 py-12 dark:bg-[linear-gradient(180deg,_#020617_0%,_#111827_45%,_#0f172a_100%)] lg:px-10">
      <div className="mx-auto grid max-w-6xl gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <SurfaceCard className="overflow-hidden bg-[linear-gradient(135deg,_#0f172a,_#111827_58%,_#1d4ed8)] text-white dark:border-white/10">
          <SectionHeader
            eyebrow={forgotMode ? "Recovery" : isLogin ? "Welcome back" : "Create account"}
            title={forgotMode ? "Recover account access securely" : isLogin ? "Enter the trading workspace" : "Start your trading journal properly"}
            description={forgotMode ? "Send a reset link by email, with a development fallback that can still expose the raw reset token when mail delivery is not configured." : isLogin ? "Sign in to review trades, analytics, journal notes, and the rest of the operating workspace." : "Register for structured journaling, stronger analytics, and a workspace designed for disciplined trading review."}
          />
          <div className="mt-8 space-y-4">
            <div className="rounded-3xl border border-white/10 bg-white/10 p-4">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 text-sky-200" />
                <div>
                  <p className="text-sm font-semibold">Structured access</p>
                  <p className="mt-1 text-sm text-slate-200">Keep journal data tied to the authenticated workspace rather than scattered across devices and notes.</p>
                </div>
              </div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/10 p-4">
              <div className="flex items-center gap-3">
                <KeyRound className="h-5 w-5 text-sky-200" />
                <div>
                  <p className="text-sm font-semibold">Credential control</p>
                  <p className="mt-1 text-sm text-slate-200">Reset and login flows are kept visible and deliberate so access issues do not block trading review for long.</p>
                </div>
              </div>
            </div>
          </div>
        </SurfaceCard>

        <SurfaceCard className="bg-white dark:bg-gray-900">
          <SectionHeader
            eyebrow={forgotMode ? "Forgot password" : isLogin ? "Login" : "Register"}
            title={forgotMode ? "Send reset token" : isLogin ? "Sign in" : "Create your account"}
            description={forgotMode ? "Enter the account email to request a reset link." : isLogin ? "Use your existing credentials to continue." : "Choose credentials strong enough for an account that may eventually hold valuable performance history."}
          />

          {!forgotMode ? (
            <>
              <form onSubmit={submit} className="mt-6 grid gap-5" aria-label={isLogin ? "Login form" : "Registration form"}>
                <Field
                  label={isLogin ? "Username or Email" : "Username"}
                  value={form.identifier}
                  onChange={e=>setForm({...form, identifier:e.target.value})}
                  placeholder={isLogin ? "username or you@example.com" : "Username"}
                  autoComplete="username"
                  required
                />
                {!isLogin ? (
                  <Field
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={e=>setForm({...form, email:e.target.value})}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                  />
                ) : null}
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">Password</span>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={e=>setForm({...form, password:e.target.value})}
                      placeholder="Password"
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-11 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-100 dark:focus:border-gray-500"
                      autoComplete={isLogin ? "current-password" : "new-password"}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-3 text-slate-500 dark:text-slate-400"
                      tabIndex={-1}
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </label>
                {!isLogin ? (
                  <>
                    <label className="block">
                      <span className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">Confirm Password</span>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={e=>setConfirmPassword(e.target.value)}
                          placeholder="Confirm password"
                          className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-11 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-100 dark:focus:border-gray-500"
                          autoComplete="new-password"
                          required
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-3 text-slate-500 dark:text-slate-400"
                          tabIndex={-1}
                          onClick={() => setShowConfirmPassword(v => !v)}
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                          {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </label>
                    <p className={`text-xs font-medium ${passwordTone}`}>Password strength: {passwordStrength || "Not entered"}</p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Use at least 8 characters with a mix of capital letters, numbers, and symbols.</p>
                  </>
                ) : null}
                <Button type="submit" variant="brand" disabled={loading}>
                  {isLogin ? <UserRound className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                  {loading ? (isLogin ? "Logging in..." : "Creating account...") : (isLogin ? "Login" : "Register")}
                </Button>
                {error ? <div className="text-sm text-rose-600" role="alert">{error}</div> : null}
                {success ? <div className="text-sm text-emerald-600" role="status">{success}</div> : null}
              </form>
              <div className="mt-5 flex flex-col gap-3">
                <button
                    onClick={() => { setIsLogin(!isLogin); setError(""); setSuccess(""); setForm({ identifier: "", password: "", email: "" }); setConfirmPassword(""); navigate(isLogin ? "/signup" : "/login"); }}
                  className="rounded-full border border-slate-200 bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100 dark:hover:bg-gray-700"
                  aria-label={isLogin ? "Switch to registration" : "Switch to login"}
                >
                  {isLogin ? "Need an account? Register" : "Already registered? Login"}
                </button>
                {isLogin ? (
                  <button
                    onClick={() => { setForgotMode(true); setForgotEmail(""); setForgotResult(null); setError(""); setSuccess(""); }}
                    className="rounded-full border border-sky-200 bg-sky-50 px-5 py-3 text-sm font-semibold text-sky-700 transition hover:bg-sky-100 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300 dark:hover:bg-sky-500/20"
                    aria-label="Forgot password"
                  >
                    Forgot password?
                  </button>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <form onSubmit={handleForgot} className="mt-6 grid gap-5" aria-label="Forgot password form">
                <Field
                  label="Account Email"
                  type="email"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                />
                <Button type="submit" variant="brand" disabled={forgotLoading}>
                  <KeyRound className="h-4 w-4" />
                  {forgotLoading ? "Sending..." : "Send Reset Link"}
                </Button>
              </form>
              {forgotResult ? (
                <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-5 text-sm dark:border-gray-700 dark:bg-gray-800">
                  {forgotResult.success ? (
                    <>
                      <p className="font-semibold text-emerald-600">{forgotResult.message || "Reset instructions sent."}</p>
                      {hasForgotDebugResetDetails ? (
                        <>
                          <p className="mt-3 break-all text-xs text-slate-600 dark:text-slate-300">Token: <span className="font-mono">{forgotResult.token}</span></p>
                          <p className="mt-2 break-all text-xs text-slate-600 dark:text-slate-300">Reset Link: <a href={forgotResult.resetLink} className="text-sky-600 underline">{forgotResult.resetLink}</a></p>
                          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">Copy the token and continue to the reset password page.</p>
                          <Button type="button" variant="brand" className="mt-4" onClick={() => navigate("/reset-password")}>Go to Reset Password</Button>
                        </>
                      ) : (
                        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">Check the inbox for this account email. The reset link expires in one hour.</p>
                      )}
                    </>
                  ) : (
                    <p className="text-rose-600">{forgotResult.error || forgotResult.message}</p>
                  )}
                </div>
              ) : null}
              <button
                onClick={() => setForgotMode(false)}
                className="mt-5 rounded-full border border-slate-200 bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100 dark:hover:bg-gray-700"
                aria-label="Back to login"
              >
                Back to login
              </button>
            </>
          )}
          <div className="mt-5 text-xs text-slate-500 dark:text-slate-400">
            By continuing, you are entering a workspace intended for disciplined journaling and performance review.
          </div>
          {!forgotMode ? (
            <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              Prefer a dedicated flow? <Link to="/forgot-password" className="font-semibold text-sky-600">Use the standalone password recovery page</Link>.
            </div>
          ) : null}
        </SurfaceCard>
      </div>
    </div>
  );
}